import { useContext, useEffect, useRef, useState, useCallback } from "react";
import { AppLayout } from "../../../../containers/layout/app";
import { LoanApplicationWrapper } from "./styled";
import { AppContext } from "../../../../context/appContext";
import { Box, Grid2, Stack, Typography } from "@mui/material";
import { BaseButton } from "../../../../components/button/styled";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import Cookies from "universal-cookie";
import { useQuery } from "@tanstack/react-query";
import { deleteLoanApplicationService } from "../../../../utils/api/loanmanagement/loansetup/deleteLoanApplication";
import { retrieveAllLoanService } from "../../../../utils/api/loanmanagement/loansetup/retrieveAllLoan";
import { LoanApplicationTable } from "../../../../containers/tables/loanapplicationtable";
import { BaseConfirmationModal } from "../../../../components/modal/confirmation";

export const LoanApplication = () => {
	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const navigate = useNavigate();
	const { setActiveNavItem } = useContext(AppContext);
	const dropdownRef = useRef<HTMLUListElement | null>(null);

	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [selectedLoan, setSelectedLoan] = useState<Record<string, any> | null>(
		null,
	);
	const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
	const [isFetching, setIsFetching] = useState(false);
	const [paginationIndex, setPaginationIndex] = useState({
		page: "1",
		perPage: "10",
		totalPages: "1",
	});

	useEffect(() => {
		setActiveNavItem("Loan Management");
	}, [setActiveNavItem]);

	const { data: loanApplications, refetch: refetchAllLoanApplication } =
		useQuery({
			queryKey: ["loanApplications", TOKEN, paginationIndex],
			queryFn: async () => {
				setIsFetching(true);
				const response = await retrieveAllLoanService(TOKEN, paginationIndex, {
					status: ["drafted"],
				});;
				if (!Array.isArray(response) && response?.meta) {
					setPaginationIndex((prev) => ({
						...prev,
						totalPages: String(response?.meta?.totalPages ?? "1"),
					}));
				}
				setIsFetching(false);
				return response.data;
			},
			enabled: !!TOKEN,
		});

	const handleBookLoanButtonClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	) => {
		e.preventDefault();
		return navigate("/loanmanagement/draftapplication");
	};

	const handleEllipsisButtonClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		loan: Record<string, any> | null,
	) => {
		e.stopPropagation();
		e.preventDefault();
		return setSelectedLoan(loan);
	};

	const handleActionItemClick = async (
		e: React.MouseEvent<HTMLLIElement, MouseEvent>,
		id: string | null,
		action: string,
	) => {
		e.stopPropagation();
		e.preventDefault();
		if (!id) return;
		switch (action) {
			case "Edit Draft":
				navigate(`/loanmanagement/application/${id}/draft`);
				break;
			case "Delete Draft":
				setError(null);
				setIsConfirmationModalOpen(true);
				break;
			case "Proceed to Book":
				navigate(`/loanmanagement/application/${id}/book`);
				break;
			default:
				break;
		}
		return;
	};

	const handleDeleteLoanApplication = async (
		loanId: string,
		institutionId: string,
	) => {
		try {
			const response = await deleteLoanApplicationService(
				TOKEN,
				loanId,
				institutionId,
			);
			if (response.status === "success") {
				setIsLoading(false);
				setSelectedLoan(null);
				setIsConfirmationModalOpen(false);
				refetchAllLoanApplication();
			} else {
				setIsLoading(false);
				setError("Loan application deletion failed. Please try again.");
			}
		} catch (error: any) {
			setIsLoading(false);
			setError(`Loan deletion failed. ${error.message}`);
			console.error("Loan application deletion failed:", error);
		}
	};

	const handleDropDownClickOutside = useCallback(
		(event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node) &&
				isConfirmationModalOpen === false
			) {
				setSelectedLoan(null);
			}
		},
		[dropdownRef, isConfirmationModalOpen],
	);

	const handleConfirmationModalCallToActionClick = async (
		e: React.MouseEvent<HTMLElement, MouseEvent>,
		actionButton: string,
	) => {
		e.preventDefault();
		e.stopPropagation();
		if (!selectedLoan?.id) return;
		switch (actionButton) {
			case "callToActionI":
				setError(null);
				setIsLoading(true);
				await handleDeleteLoanApplication(
					selectedLoan?.id,
					selectedLoan?.institution,
				);
				break;
			case "callToActionII":
				setIsLoading(false);
				setError(null);
				setSelectedLoan(null);
				setIsConfirmationModalOpen(false);
				break;
			default:
				break;
		}
	};

	const handleConfirmationModalPersist = () => {
		return setIsConfirmationModalOpen(true);
	};

	const handlePagination = (
		e: React.MouseEvent<HTMLButtonElement>,
		type: "previous" | "next",
	) => {
		e.preventDefault();
		setPaginationIndex((prev) => {
			const currentPage = parseInt(prev.page, 10);
			const newPage =
				type === "previous"
					? Math.max(1, currentPage - 1)
					: Math.min(parseInt(prev.totalPages, 10), currentPage + 1);
			return {
				...prev,
				page: newPage.toString(),
			};
		});
	};

	useEffect(() => {
		if (selectedLoan !== null) {
			document.addEventListener("mousedown", handleDropDownClickOutside);
		} else {
			document.removeEventListener("mousedown", handleDropDownClickOutside);
		}
		return () => {
			document.removeEventListener("mousedown", handleDropDownClickOutside);
		};
	}, [selectedLoan, handleDropDownClickOutside]);

	const confirmationModalBody = error ? (
		<Box>
			<Typography
				fontFamily={"Inter"}
				fontWeight={"600"}
				fontSize={14}
				lineHeight={"normal"}
				color={"var(--text-salmon-pink-color)"}
				whiteSpace={"normal"}
				marginBlockStart={"calc(var(--basic-padding) / 1.3)"}
			>
				{error}
			</Typography>
		</Box>
	) : null;

	return (
		<AppLayout pageId={"Book-Loan"} pageTitle={"Loan Applications"}>
			<LoanApplicationWrapper>
				{/* alert modal below */}
				<BaseConfirmationModal
					open={isConfirmationModalOpen}
					title="Confirm Action"
					message="Are you sure you want to delete this application?"
					callToActionI="Confirm"
					callToActionII="Cancel"
					isLoadingI={isLoading}
					body={confirmationModalBody}
					handleClose={handleConfirmationModalPersist}
					handleCallToAction={handleConfirmationModalCallToActionClick}
				/>
				{/* alert modal above */}
				<Box>
					<BaseButton
						variant="contained"
						bgcolor="var(--primary-color)"
						padding="calc(var(--basic-padding)/4) calc(var(--basic-padding))"
						startIcon={<AddIcon />}
						sx={{
							width: { mobile: "-webkit-fill-available", miniTablet: "auto" },
							float: { miniTablet: "right" },
						}}
						onClick={handleBookLoanButtonClick}
					>
						<Typography
							variant={"button"}
							fontFamily={"inherit"}
							fontWeight={"inherit"}
							fontSize={"inherit"}
							lineHeight={"inherit"}
							color={"inherit"}
							textTransform={"inherit"}
						>
							Book Loan
						</Typography>
					</BaseButton>
				</Box>
				<Box component={"div"} className="loan-application-table-box">
					<LoanApplicationTable
						rows={loanApplications ?? []}
						dropdownRef={dropdownRef}
						selectedLoan={selectedLoan}
						handleActionItemClick={handleActionItemClick}
						handleEllipsisButtonClick={handleEllipsisButtonClick}
					/>
				</Box>
				<Grid2 container component={"div"} spacing={"var(--flex-gap)"}>
					<Grid2
						size={{ mobile: 12, laptop: 8 }}
						display={{ mobile: "none", laptop: "grid" }}
					></Grid2>
					<Grid2 size={{ mobile: 12, laptop: 4 }}>
						<Stack
							direction={"row"}
							overflow={"hidden"}
							borderRadius={"6px"}
							alignItems={"center"}
							justifyContent={"space-around"}
							border={"1px solid var(--input-field-border-color)"}
						>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									disabled={
										parseInt(paginationIndex.page, 10) === 1 || isFetching
									}
									onClick={(e) => handlePagination(e, "previous")}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Previous
									</Typography>
								</BaseButton>
							</Box>
							<Box
								display={"flex"}
								overflow={"hidden"}
								bgcolor={"var(--primary-color)"}
								padding={"calc(var(--basic-padding)/2)"}
							>
								<Typography
									variant="h3"
									fontFamily={"Roboto"}
									fontWeight={600}
									fontSize={10}
									lineHeight={"normal"}
									color="var(--light-color)"
								>
									{`${paginationIndex.page} / ${paginationIndex.totalPages}`}
								</Typography>
							</Box>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									onClick={(e) => handlePagination(e, "next")}
									disabled={
										paginationIndex.page === paginationIndex.totalPages ||
										isFetching
									}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Next
									</Typography>
								</BaseButton>
							</Box>
						</Stack>
					</Grid2>
				</Grid2>
			</LoanApplicationWrapper>
		</AppLayout>
	);
};
